import React from 'react';
import Input from '@/components/forms/Input';

export const formatMinutesToHourString = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);

    if (m === 0 && h <= 24) {
        return `${h}h`;
    }

    return `${h}h${m.toString().padStart(2, '0')}`;
};

export const parseHourStringToMinutes = (value: string): number | null => {
    if (!value) {
        return null;
    }

    if (value.includes('h') || value.includes(':')) {
        const parts = value.toLowerCase().split(/[h:]/);
        const h = Number.parseInt(parts[0] || '0', 10);
        const m = Number.parseInt(parts[1] || '0', 10);
        return h * 60 + m;
    }

    const floatVal = Number.parseFloat(value);

    if (Number.isNaN(floatVal)) {
        return null;
    }

    return Math.round(floatVal * 60);
};

interface HourInputProps {
    valueMinutes: number;
    onChange: (val: number) => void;
    disabled: boolean;
    className?: string;
    ariaLabel?: string;
}

export default function HourInput({
                                      valueMinutes,
                                      onChange,
                                      disabled,
                                      className,
                                      ariaLabel
                                  }: Readonly<HourInputProps>) {
    const [localValue, setLocalValue] = React.useState(formatMinutesToHourString(valueMinutes));

    React.useEffect(() => {
        setLocalValue(formatMinutesToHourString(valueMinutes));
    }, [valueMinutes]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value;


        const separators = (val.match(/[h:.]/g) || []).length;

        if (separators > 1) {
            return;
        }

        if (/^\d{0,4}$/.test(val) || /^\d{1,2}[h:.]\d{0,2}$/.test(val)) {
            setLocalValue(val);
        }
    };

    const handleBlur = () => {
        let value = localValue;

        if (/^\d{3,4}$/.test(value)) {
            const padded = value.padStart(4, '0');
            value = `${padded.slice(0, 2)}h${padded.slice(2)}`;
        }

        const minutes = parseHourStringToMinutes(value);

        if (minutes !== null && !Number.isNaN(minutes) && minutes >= 0 && minutes <= 1499) {
            onChange(minutes);
            setLocalValue(formatMinutesToHourString(minutes));
        } else {
            setLocalValue(formatMinutesToHourString(valueMinutes));
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.currentTarget.blur();
        }
    };

    return (
        <Input
            type="text"
            value={localValue}
            disabled={disabled}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={className}
            aria-label={ariaLabel}
        />
    );
}
